import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBlockedUserIds } from "@/hooks/hub/useHubBlocks";

/**
 * LR HUB "Recent Activity" rail box.
 *
 * Data source: `hub_posts` — approved, non-deleted posts across ALL feeds,
 * newest first, with the joined feed label. Read-only; no write path here.
 *
 * ISOLATION: hub_posts / hub_feeds only.
 */

export interface HubRecentPost {
  id: string;
  author_id: string;
  body: string;
  created_at: string;
  feed: { slug: string; name: string } | null;
}

export function useHubRecentPosts(limit = 20) {
  const { blockedIds } = useBlockedUserIds();
  return useQuery({
    queryKey: ["hub-recent-posts", limit, blockedIds.join(",")],
    staleTime: 60_000,
    queryFn: async (): Promise<HubRecentPost[]> => {
      let q = supabase
        .from("hub_posts")
        .select("id, author_id, body, created_at, feed:hub_feeds(slug, name)")
        .eq("is_deleted", false)
        .eq("moderation_status", "approved")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (blockedIds.length) q = q.not("author_id", "in", `(${blockedIds.join(",")})`);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as HubRecentPost[];
    },
  });
}

/** First line of a post body, collapsed and truncated to one short line. */
export function hubPostSnippet(body: string, max = 70): string {
  const line = (body ?? "").split(/\r?\n/).find((l) => l.trim().length > 0) ?? "";
  const clean = line.trim().replace(/\s+/g, " ");
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

export default useHubRecentPosts;
